import React from "react";
import { Tooltip } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";

const S = {
  passBg: "#EAF3DE",
  passText: "#3B6D11",
  failBg: "#FCEBEB",
  failText: "#A32D2D",
  blueBg: "#E6F1FB",
  blueText: "#185FA5",
  ambBg: "#FAEEDA",
  ambText: "#854F0B",
  grayBg: "#F1EFE8",
  grayText: "#5F5E5A",
};

/**
 * Dark horizontal banner showing labelled stat items plus a segmented bar.
 *
 * items:    [{ label, value, color? }]
 * segments: [{ value, color }] — ordered pass, fail, skip/stopped
 * passRateLabel: optional string (defaults to "Pass rate")
 */
export function StatsStrip({ items, segments = [], passRateLabel = "Pass rate" }) {
  const total = segments.reduce((sum, s) => sum + (s.value || 0), 0) || 1;
  const passRate = Math.round(((segments[0]?.value || 0) / total) * 100);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        background: "#0B1F3A",
        borderRadius: 10,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      {items.map(({ label, value, color }) => (
        <div
          key={label}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "10px 20px",
            borderRight: "0.5px solid rgba(255,255,255,0.15)",
          }}
        >
          <span
            style={{
              fontSize: 22,
              fontWeight: 500,
              color: color || "#fff",
              lineHeight: 1.2,
            }}
          >
            {value}
          </span>
          <span
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.65)",
              marginTop: 3,
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
        </div>
      ))}
      <div style={{ flex: 1, padding: "12px 20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 11,
            color: "rgba(255,255,255,0.55)",
            marginBottom: 5,
          }}
        >
          <span>{passRateLabel}</span>
          <span style={{ fontWeight: 500, color: "#fff" }}>{passRate}%</span>
        </div>
        <div
          style={{
            height: 6,
            background: "rgba(255,255,255,0.12)",
            borderRadius: 3,
            overflow: "hidden",
            display: "flex",
          }}
        >
          {segments.map(({ value, color }, i) => (
            <div
              key={i}
              style={{
                width: `${(value / total) * 100}%`,
                height: "100%",
                background: color,
                transition: "width .4s",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Compact horizontal pass-rate bar used in table columns. */
export function MiniBar({ pass, total }) {
  if (!total) return <span style={{ color: "#bbb", fontSize: 12 }}>—</span>;
  const pct = Math.round((pass / total) * 100);
  const color = pct === 100 ? "#3B6D11" : pct > 50 ? "#fa8c16" : "#A32D2D";
  return (
    <Tooltip title={`${pass} / ${total} passed (${pct}%)`}>
      <div
        style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 80 }}
      >
        <div
          style={{
            flex: 1,
            height: 6,
            background: "#f0f0f0",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <div
            style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 3 }}
          />
        </div>
        <span
          style={{ fontSize: 11, color: "#888", minWidth: 28, textAlign: "right" }}
        >
          {pct}%
        </span>
      </div>
    </Tooltip>
  );
}

/** Coloured pill badge for Pass / Fail / Partial / Stopped / Error / Skipped statuses. */
export function StatusBadge({ status }) {
  if (!status) status = "Fail";
  const cfg = {
    Pass: { bg: S.passBg, color: S.passText, icon: <CheckCircleOutlined />, label: "Passed" },
    Fail: { bg: S.failBg, color: S.failText, icon: <CloseCircleOutlined />, label: "Failed" },
    Stopped: { bg: S.grayBg, color: S.grayText, icon: <StopOutlined />, label: "Stopped" },
    Error: { bg: S.ambBg, color: S.ambText, icon: <MinusCircleOutlined />, label: "Error" },
    Skip: { bg: S.grayBg, color: S.grayText, icon: <MinusCircleOutlined />, label: "Skipped" },
    // Fan-out: a mixed run is amber, not red — some branches did pass. "Skipped" is the
    // backend's word for a step whose upstream failed; "Skip" above is the older test-level one.
    Partial: { bg: S.ambBg, color: S.ambText, icon: <MinusCircleOutlined />, label: "Partial" },
    Skipped: { bg: S.grayBg, color: S.grayText, icon: <MinusCircleOutlined />, label: "Skipped" },
  }[status] || { bg: S.grayBg, color: S.grayText, icon: <MinusCircleOutlined />, label: status };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: cfg.bg,
        color: cfg.color,
        padding: "2px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.icon} {cfg.label}
    </span>
  );
}
